'use client';

import { useState } from 'react';
import { ResultExport } from '@/components/tools/ResultExport';
import { quoteExport } from '@/lib/seo/result-exports';
import Link from 'next/link';
import { NumberField } from '@/components/product/NumberField';
import { quoteFromCosts, type QuoteInputs } from '@/lib/seo/campaign-tools';
import s from './CampaignTools.module.css';

export function QuoteCostWorksheet() {
  const [input, setInput] = useState<QuoteInputs>({
    hours: '',
    hourlyRate: '',
    expenses: '',
    publication: '',
    usage: '',
    exclusivity: '',
  });
  const result = quoteFromCosts(input);
  const pounds = (value: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  const exportText = quoteExport(input);
  return (
    <section className={`${s.form} ${s.worksheet}`} aria-labelledby="cost-worksheet-title">
      <h2 id="cost-worksheet-title">Build a quote from your own figures</h2>
      <p className={s.hint}>
        Include planning, filming, editing, agreed revisions and reporting in your hours. Choose
        your own hourly rate and any fees for usage or exclusivity. Enter £0 for a cost you have
        deliberately left out.
      </p>
      <div className={s.fields}>
        {(
          [
            ['hours', 'Production hours', undefined],
            ['hourlyRate', 'Your hourly rate', '£'],
            ['publication', 'Publication fee', '£'],
            ['expenses', 'Expenses', '£'],
            ['usage', 'Usage fee', '£'],
            ['exclusivity', 'Exclusivity fee', '£'],
          ] as const
        ).map(([key, label, prefix]) => (
          <NumberField
            key={key}
            label={label}
            prefix={prefix}
            min={0}
            step={0.01}
            decimalScale={2}
            allowNegative={false}
            clampBehavior="none"
            trimLeadingZeroesOnBlur={false}
            inputMode="decimal"
            value={input[key]}
            onChange={(value) => setInput({ ...input, [key]: String(value) })}
          />
        ))}
      </div>
      <div className={s.result} role="status" aria-live="polite">
        {result ? (
          <>
            <span>Quote subtotal from your inputs</span>
            <strong className={s.amount}>{pounds(result.total)}</strong>
            <p className={s.hint}>
              Production: {pounds(result.production)}. Publication:{' '}
              {pounds(Number(input.publication))}. Expenses: {pounds(Number(input.expenses))}.
              Usage: {pounds(Number(input.usage))}. Exclusivity: {pounds(Number(input.exclusivity))}
              .
            </p>
          </>
        ) : (
          <p className={s.hint}>Complete all six fields to see the subtotal.</p>
        )}
      </div>
      {exportText ? (
        <ResultExport
          text={exportText}
          filename="creator-quote-subtotal"
          tool="quote-cost-worksheet"
        />
      ) : null}
      <p className={s.hint}>
        Hours × hourly rate + publication fee + expenses + usage fee + exclusivity fee. This adds
        the amounts you supply; it does not value your audience or recommend a market rate. Check
        whether your chosen rate covers overheads and profit, and state any tax separately.
      </p>
      <Link href="/influencer-rate-card-guide">
        Define the deliverables and scope in the rate card guide →
      </Link>
    </section>
  );
}
